import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, CardHeader, Heading, VStack, HStack, Switch, FormControl,
  FormLabel, FormHelperText, Text, Badge, Tag, TagLabel, TagCloseButton,
  Input, Button, useToast, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Box, Alert, AlertIcon, Spinner,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import settingsService from '../services/settingsService';

/**
 * Settings → Communications — the per-hotel hold that stops every
 * outgoing message (email/SMS/OTA) while a property is still onboarding.
 * Enforcement lives in the backend (core/email_guard.py); this card is
 * read/write + visibility into what got held (HeldMessage rows).
 */
const CommunicationsHoldCard = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [allowInput, setAllowInput] = useState('');
  const [allowError, setAllowError] = useState('');
  const [held, setHeld] = useState([]);
  const [heldLoading, setHeldLoading] = useState(false);

  const load = async () => {
    try {
      const res = await settingsService.getCommunicationsHold();
      setData(res);
      setNote(res.note || '');
    } catch {
      toast({ title: t('communicationsHold.toasts.loadFailed'), status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const loadHeld = async () => {
    setHeldLoading(true);
    try {
      const res = await settingsService.getHeldMessages(50);
      setHeld(res.results || []);
    } catch {
      toast({ title: t('communicationsHold.toasts.heldLoadFailed'), status: 'error', duration: 3000 });
    } finally {
      setHeldLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHeld();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch, { refreshHeld = false } = {}) => {
    setSaving(true);
    try {
      const res = await settingsService.updateCommunicationsHold(patch);
      setData(res);
      toast({ title: t('communicationsHold.toasts.updated'), status: 'success', duration: 2000 });
      if (refreshHeld) loadHeld();
      return true;
    } catch (err) {
      toast({
        title: t('communicationsHold.toasts.saveFailed'),
        description: err.response?.data?.detail,
        status: 'error', duration: 3000,
      });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (e) => save({ hold: e.target.checked }, { refreshHeld: true });

  const handleAddAllow = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const value = allowInput.trim();
    if (!value) return;
    const next = [...(data.allow || []), value];
    const ok = await save({ allow: next });
    if (ok) {
      setAllowInput('');
      setAllowError('');
    } else {
      setAllowError(t('communicationsHold.card.allowInvalid', { value }));
    }
  };

  const handleRemoveAllow = (value) => {
    const next = (data.allow || []).filter((a) => a !== value);
    save({ allow: next });
  };

  const handleNoteBlur = () => {
    if (note !== (data?.note || '')) save({ note });
  };

  if (loading) {
    return (
      <Card shadow="sm" borderWidth="1px">
        <CardBody><Spinner size="sm" /></CardBody>
      </Card>
    );
  }
  if (!data) return null;

  return (
    <Card shadow="sm" borderWidth="1px" borderColor={data.hold ? 'orange.300' : undefined}>
      <CardHeader pb={2}>
        <HStack justify="space-between">
          <Heading size="sm">{t('communicationsHold.card.title')}</Heading>
          {data.heldCount > 0 && (
            <Badge colorScheme="orange">{data.heldCount}</Badge>
          )}
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack align="stretch" spacing={4}>
          <HStack align="flex-start" spacing={3}>
            <Switch
              id="comms-hold-switch"
              isChecked={!!data.hold}
              onChange={handleToggle}
              isDisabled={saving}
              mt={1}
              colorScheme="orange"
            />
            <Box>
              <FormLabel htmlFor="comms-hold-switch" mb={0} fontWeight="semibold">
                {t('communicationsHold.card.switchLabel')}
              </FormLabel>
              <Text fontSize="sm" color="gray.600" mt={1}>
                {t('communicationsHold.card.switchHelp')}
              </Text>
              {data.hold && data.since && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('communicationsHold.card.since', { date: new Date(data.since).toLocaleString() })}
                </Text>
              )}
            </Box>
          </HStack>

          {data.onboardingCompleted && data.hold && (
            <Alert status="info" borderRadius="md" fontSize="sm">
              <AlertIcon />
              {t('communicationsHold.card.onboardingDoneNudge')}
            </Alert>
          )}

          <FormControl isInvalid={!!allowError}>
            <FormLabel fontSize="sm">{t('communicationsHold.card.allowLabel')}</FormLabel>
            {(data.allow || []).length > 0 && (
              <HStack wrap="wrap" mb={2} spacing={2} rowGap={2}>
                {data.allow.map((value) => (
                  <Tag key={value} size="md" colorScheme="blue" borderRadius="full">
                    <TagLabel>{value}</TagLabel>
                    <TagCloseButton onClick={() => handleRemoveAllow(value)} isDisabled={saving} />
                  </Tag>
                ))}
              </HStack>
            )}
            <Input
              size="sm"
              placeholder={t('communicationsHold.card.allowPlaceholder')}
              value={allowInput}
              onChange={(e) => { setAllowInput(e.target.value); setAllowError(''); }}
              onKeyDown={handleAddAllow}
              isDisabled={saving}
            />
            <FormHelperText color={allowError ? 'red.500' : undefined}>
              {allowError || t('communicationsHold.card.allowHelp')}
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel fontSize="sm">{t('communicationsHold.card.noteLabel')}</FormLabel>
            <Input
              size="sm"
              value={note}
              placeholder={t('communicationsHold.card.notePlaceholder')}
              onChange={(e) => setNote(e.target.value)}
              onBlur={handleNoteBlur}
              isDisabled={saving}
            />
          </FormControl>

          <Box>
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="semibold" fontSize="sm">{t('communicationsHold.card.heldTitle')}</Text>
              <HStack spacing={2}>
                <Badge>{data.heldCount}</Badge>
                <Button size="xs" variant="ghost" onClick={loadHeld} isLoading={heldLoading}>
                  {t('common.refresh')}
                </Button>
              </HStack>
            </HStack>
            {heldLoading ? (
              <Spinner size="sm" />
            ) : held.length === 0 ? (
              <Text fontSize="sm" color="gray.500">{t('communicationsHold.card.heldEmpty')}</Text>
            ) : (
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('communicationsHold.card.heldColumns.when')}</Th>
                      <Th>{t('communicationsHold.card.heldColumns.channel')}</Th>
                      <Th>{t('communicationsHold.card.heldColumns.recipients')}</Th>
                      <Th>{t('communicationsHold.card.heldColumns.subject')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {held.map((row) => (
                      <Tr key={row.id}>
                        <Td whiteSpace="nowrap" fontSize="xs">{new Date(row.createdAt).toLocaleString()}</Td>
                        <Td fontSize="xs">
                          {t(`communicationsHold.card.channels.${row.channel}`, row.channel)}
                        </Td>
                        <Td fontSize="xs">{(row.recipients || []).join(', ')}</Td>
                        <Td fontSize="xs" maxW="320px" isTruncated title={row.preview}>
                          {row.subject || row.preview}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </VStack>
      </CardBody>
    </Card>
  );
};

export default CommunicationsHoldCard;
